import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnyFieldApi, AnyFormApi } from '@tanstack/react-form';
import { Panel, PanelSection } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { PersonSelectorField } from './PersonSelector';
import { RESPONSIBILITY_ROLE_FIELDS } from '../responsibleRoles';
import { FORM_LABEL_WIDTH } from '../formLayout';

/**
 * Minimální tvar `form.Field`, který panel potřebuje. Formuláře projektu, série i produktu mají odlišné
 * hodnoty, ale **shodný podstrom `responsiblePersons.<role>`**, takže panel funguje napříč nimi.
 */
type FieldRenderer = (props: {
  name: string;
  children: (field: AnyFieldApi) => ReactNode;
}) => ReactElement;

interface ResponsiblePersonsPanelProps {
  /** Libovolný TanStack form s podstromem `responsiblePersons.<role>` (projekt / série / produkt). */
  form: AnyFormApi;
  /** Titulek panelu. */
  title: string;
  /** Nápověda pod titulkem. */
  help: string;
  /** Placeholder person selectoru. */
  placeholder: string;
  /** Prefix testId jednotlivých rolí (`<prefix>-person-<key>`). */
  testIdPrefix: string;
  /** Volitelné třídy na obalu obsahu (např. `max-w-220` u full-width oken). */
  contentClassName?: string;
}

/**
 * Sdílený panel „Odpovědné osoby" — 4 role jako single-column inline `PersonSelectorField`. Používá ho editace
 * projektu i create formuláře série/produktu, aby byly osoby vždy ve vlastním panelu se stejným vzhledem.
 */
export function ResponsiblePersonsPanel({
  form,
  title,
  help,
  placeholder,
  testIdPrefix,
  contentClassName,
}: ResponsiblePersonsPanelProps) {
  const { t } = useTranslation('venua-planner');
  // `AnyFormApi` (FormApi<any>) nenese React binding `.Field`; podstrom `responsiblePersons` je ale u všech
  // konzumentů shodný, tak si `.Field` vytáhneme přes jeden izolovaný cast (minimální escape hatch).
  const Field = (form as unknown as { Field: FieldRenderer }).Field;

  return (
    <Panel variant="filled" title={title}>
      <div className={cn('w-full', contentClassName)}>
        <PanelSection>
          <p className="text-phx-text-muted text-phx-xs">{help}</p>
          <div className="gap-phx-3 flex flex-col">
            {RESPONSIBILITY_ROLE_FIELDS.map((roleField) => (
              <Field key={roleField.role} name={`responsiblePersons.${roleField.key}`}>
                {(field) => (
                  <PersonSelectorField
                    field={field}
                    mode="single"
                    label={t(`project.roles.${roleField.key}`)}
                    layout="inline"
                    labelWidth={FORM_LABEL_WIDTH}
                    placeholder={placeholder}
                    testId={`${testIdPrefix}-person-${roleField.key}`}
                  />
                )}
              </Field>
            ))}
          </div>
        </PanelSection>
      </div>
    </Panel>
  );
}
